/**
 * Point & Figure — column-stacking geometry.
 *
 * WHY A SEPARATE MODULE AND NOT AN ALIAS OF THE RENKO PATH (2026-09-29).
 * A Point & Figure column puts EVERY box it contains at ONE x, while
 * `KernelEngine.toBarPoints` gives every BAR its own x
 * (`indexToPixelX(fromIndex + index + 0.5)`). Aliasing P&F onto the renko
 * rectangle path therefore smears one column across N bar slots, and the gap
 * widens with every bar the column spends not forming a box. That is not a
 * tuning problem, it is the wrong primitive, so this mode has its own layout
 * function instead of a mode alias.
 *
 * THE PRODUCER CONTRACT (see `exoticChartData.ts` in the host repo). Each bar
 * that forms one or more boxes writes:
 *   - `boxes`          — `{ open, close }[]`, each box exactly one box-size tall
 *   - `pnfColumnStart` — the ABSOLUTE bar index at which the column began
 *   - `pnfUp`          — column direction, for the fill
 * A bar that forms no box writes nothing: it belongs to the running column but
 * has no geometry, so it must not create a column. `pnfColumnStart` therefore
 * does NOT need to be propagated to box-less bars — the bars that DO draw carry
 * the x of their own column.
 *
 * INVARIANT: every rectangle produced for one `pnfColumnStart` shares that
 * column's single x. `pointAndFigureColumns.test.ts` pins it, and the runtime
 * proof in `exoticRenderRuntime.test.ts` measures it off the canvas.
 */
import { SAFE_MAX_BAR_WIDTH } from "./zoomConstants";
/** Point & Figure colours — identical to `PointAndFigureSeries.defaultProps.fill`. */
export const PNF_UP = "#6BA583";
export const PNF_DOWN = "#E60000";
function num(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}
/**
 * Column width, same clamp shape as `KernelEngine.resolveBarWidth` (a floor of 1px,
 * an auto cap below the full step, and a global maximum) with a higher fill ratio:
 * P&F columns form a continuous staircase, so they should nearly touch.
 */
function resolveColumnWidth(columnCount, plotWidth) {
    if (!(columnCount > 0) || !(plotWidth > 0)) {
        return 0;
    }
    const step = plotWidth / columnCount;
    const maxAutoWidth = Math.max(1, step * 0.95);
    return Math.min(Math.max(1, step * 0.9), maxAutoWidth, SAFE_MAX_BAR_WIDTH);
}
/**
 * `pnfColumnStart` of each bar in the window that carries boxes, in encounter
 * order and de-duplicated. Returns `undefined` for a bar whose `pnfColumnStart`
 * is missing or not a finite number — the datum is then skipped rather than
 * guessed at, because a wrong x silently stacks a column on a neighbour.
 */
function readColumnStarts(data, fromIndex, count) {
    const seen = new Set();
    const starts = [];
    const firstStartByOffset = new Array(count).fill(Number.NaN);
    for (let offset = 0; offset < count; offset += 1) {
        const datum = data[fromIndex + offset];
        if (datum === undefined || !Array.isArray(datum["boxes"])) {
            continue;
        }
        const start = num(datum["pnfColumnStart"]);
        if (start === null) {
            continue;
        }
        if (!seen.has(start)) {
            seen.add(start);
            starts.push(start);
        }
        firstStartByOffset[offset] = start;
    }
    return { starts, firstStartByOffset };
}
/**
 * Build the rectangles for a Point & Figure pane.
 *
 * `indexToPixelX` is `PaneProjector.indexToPixelX`; a column start outside the
 * visible window still projects (to a negative or over-wide x) and the plot-area
 * clip in the render loop trims it, which is what keeps a column that began
 * before the window pinned at its real x instead of snapping to the edge.
 */
export function layoutPointAndFigure(options) {
    const { data, fromIndex, count, indexToPixelX, plotWidth } = options;
    const rects = [];
    const { starts, firstStartByOffset } = readColumnStarts(data, fromIndex, count);
    const columnWidth = resolveColumnWidth(starts.length, plotWidth);
    if (columnWidth <= 0) {
        return { rects, columnWidth, columnCount: starts.length };
    }
    // One projection per column, not per box: the whole point of the mode.
    const xByStart = new Map();
    for (const start of starts) {
        xByStart.set(start, indexToPixelX(start + 0.5));
    }
    for (let offset = 0; offset < count; offset += 1) {
        const datum = data[fromIndex + offset];
        if (datum === undefined) {
            continue;
        }
        const boxes = datum["boxes"];
        if (!Array.isArray(boxes) || boxes.length === 0) {
            continue;
        }
        const x = xByStart.get(firstStartByOffset[offset]);
        if (x === null || x === undefined || !Number.isFinite(x)) {
            continue;
        }
        const fill = datum["pnfUp"] === true ? PNF_UP : PNF_DOWN;
        for (const raw of boxes) {
            const box = raw;
            const open = box === null || box === undefined ? null : num(box["open"]);
            const close = box === null || box === undefined ? null : num(box["close"]);
            // A zero-height box is invisible and `BarRenderer` would skip it anyway;
            // dropping it here keeps the rect list equal to the visible geometry.
            if (open === null || close === null || open === close) {
                continue;
            }
            rects.push({ x, value: close, baseline: open, paletteColor: fill });
        }
    }
    return { rects, columnWidth, columnCount: starts.length };
}
/**
 * Lowest and highest box edge of the window, or `null` when the window has no
 * box at all.
 *
 * Called at the DOMAIN seam, not the render seam, and that is the reason this
 * exists: the P&F accessor reports one scalar per bar — the leading edge of the
 * column at that bar — and `createDomainFromSeriesValues` takes min/max of those
 * scalars. For a column that began before the window, the leading edge alone is a
 * strict subset of the drawn geometry, so without this the bottom box of every
 * column is drawn below the domain and clipped away.
 */
export function resolvePointAndFigureExtent(data, fromIndex, count) {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (let offset = 0; offset < count; offset += 1) {
        const boxes = data[fromIndex + offset]?.["boxes"];
        if (!Array.isArray(boxes)) {
            continue;
        }
        for (const raw of boxes) {
            const box = raw;
            if (box === null || box === undefined) {
                continue;
            }
            const open = num(box["open"]);
            const close = num(box["close"]);
            if (open === null || close === null) {
                continue;
            }
            min = Math.min(min, open, close);
            max = Math.max(max, open, close);
        }
    }
    return min > max ? null : [min, max];
}
/**
 * Widen `domain` in place to cover every box in the window, and return the same
 * tuple so the call site can use it either way. Idempotent.
 */
export function widenDomainForPointAndFigure(domain, data, fromIndex, count) {
    const extent = resolvePointAndFigureExtent(data, fromIndex, count);
    if (extent === null) {
        return domain;
    }
    domain[0] = Math.min(domain[0], extent[0]);
    domain[1] = Math.max(domain[1], extent[1]);
    return domain;
}
